import { Size } from "@repo/ui/helpers/helpers";
import type { FC } from "react";

export interface GradingFeedbackCircleProps {
  correctCount: number;
  closeCount?: number;
  incorrectCount: number;
  size: typeof Size.SMALL | typeof Size.MEDIUM;
}

export const GradingFeedbackCircle: FC<GradingFeedbackCircleProps> = ({
  correctCount,
  closeCount = 0,
  incorrectCount,
  size,
}) => {
  const total = correctCount + closeCount + incorrectCount;
  if (total === 0) return null;

  const correctPercentage = (correctCount / total) * 100;
  const closePercentage = (closeCount / total) * 100;
  const incorrectPercentage = (incorrectCount / total) * 100;

  // Size configurations
  const sizeConfig = {
    [Size.SMALL]: {
      viewBox: 16,
      radius: 6,
      strokeWidth: 2,
      className: "h-4 w-4",
    },
    [Size.MEDIUM]: {
      viewBox: 24,
      radius: 9,
      strokeWidth: 3,
      className: "h-6 w-6",
    },
  };

  const { viewBox, radius, strokeWidth, className } = sizeConfig[size];
  const center = viewBox / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative flex items-center justify-center">
      <svg className={className} viewBox={`0 0 ${viewBox} ${viewBox}`}>
        {/* Background circle - only show if not 100% filled */}
        {correctPercentage + closePercentage + incorrectPercentage < 100 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
        )}
        {/* Green arc for correct */}
        {correctPercentage > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#10b981"
            strokeWidth={strokeWidth}
            strokeDasharray={`${(correctPercentage / 100) * circumference} ${circumference}`}
            strokeDashoffset="0"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
        {/* Yellow arc for close (off by 1) - position after green */}
        {closePercentage > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#eab308"
            strokeWidth={strokeWidth}
            strokeDasharray={`${(closePercentage / 100) * circumference} ${circumference}`}
            strokeDashoffset={`${-(correctPercentage / 100) * circumference}`}
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
        {/* Red arc for incorrect - position after green and yellow */}
        {incorrectPercentage > 0 && (
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#ef4444"
            strokeWidth={strokeWidth}
            strokeDasharray={`${(incorrectPercentage / 100) * circumference} ${circumference}`}
            strokeDashoffset={`${-((correctPercentage + closePercentage) / 100) * circumference}`}
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
      </svg>
      <span className="sr-only">
        {correctCount} correct{closeCount > 0 && `, ${closeCount} close`},
        {incorrectCount} incorrect
      </span>
    </div>
  );
};
